import TokenAvatar from '@components/TokenAvatar'
import { ActivityIcon, ActivityLabel } from '@modules/UserActivity/Components/ActivityListItem'
import { useUserActivities } from '@plug-core/hooks/useUserActivities'
import { PlugUserActivity } from '@plug-core/interfaces/transactionActivity'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { useSpotlightShortcut } from '@providers/SpotlightProvider'
import { ListGroup, ListRow, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { FC, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const ActivityItem = ({
  activity,
  onClick,
}: {
  activity: PlugUserActivity
  onClick: () => void
}) => {
  const received = activity.isSwap ? activity.received : activity.sent
  const amount = e8sToDecimals(received.amount, received.token.decimals)

  return (
    <ListRow
      dense
      onClick={onClick}
      leading={<TokenAvatar logo={received.token.logo} size={32} />}
      primary={
        <span className="flex items-center gap-1 text-fg">
          <ActivityIcon activity={activity} />
          <ActivityLabel activity={activity} />
        </span>
      }
      trailing={
        <Text
          as="span"
          variant="body-s"
          weight="medium"
          tone="fg-2"
          tabular
          className="whitespace-nowrap"
        >
          {activity.isOutgoing ? '-' : '+'}
          {formatNumber(amount, received.token.symbol)}
        </Text>
      }
    />
  )
}

export const RecentActivity: FC = () => {
  const { userActivity, reload } = useUserActivities(undefined, 30)
  const { open } = useSpotlightShortcut()
  const navigate = useNavigate()

  useEffect(() => {
    const interval = setInterval(reload, 15 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  const items = (userActivity || []).slice(0, 10)

  return (
    <aside className="flex h-full w-[320px] shrink-0 flex-col px-4">
      <Text as="h2" variant="label" tone="fg-2" className="mb-3 px-1 text-[15px] font-semibold">
        Recent Activity
      </Text>

      {items.length > 0 && (
        <ListGroup>
          {items.map((activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              onClick={() =>
                !window.location.href.endsWith(ROUTES.USER_ACTIVITY) &&
                navigate(ROUTES.USER_ACTIVITY)
              }
            />
          ))}
        </ListGroup>
      )}

      <button
        type="button"
        onClick={open}
        className="mt-auto mb-4 ml-auto cursor-pointer rounded-xs border-0 bg-transparent p-1 font-text text-[14px] text-fg-2 focus-visible:outline-2 focus-visible:outline-focus"
      >
        Use{' '}
        <kbd className="rounded-xs border border-hairline bg-chip px-1.5 py-0.5 font-mono text-[12px] font-semibold text-fg">
          Ctrl + P
        </kbd>{' '}
        to search
      </button>
    </aside>
  )
}

export default RecentActivity
